/* eslint-disable react/display-name */

'use client';

import React, {
  memo,
  useState,
  useCallback,
  useEffect,
  forwardRef,
  ForwardedRef,
} from 'react';

import { BILogoRes } from '@wconcept/wconcept-ui-renewal/@types/popup';
import Lottie from 'react-lottie-player';

import Button from '@wconcept/wconcept-ui-renewal/components/atoms/Button';
import Title from '@wconcept/wconcept-ui-renewal/components/atoms/Title';
import Count from '@wconcept/wconcept-ui-renewal/components/molecules/Count';
import Img from '@wconcept/wconcept-ui-renewal/components/atoms/Img';
import ModalBackBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ModalBackBtn';
import ModalHomeBtn from '@wconcept/wconcept-ui-renewal/components/molecules/_btn/ModalHomeBtn';
import {
  LogoWcon01,
  IconSearch01,
  IconCart01,
  IconAlarm01,
  IconProfile01,
} from '@wconcept/wconcept-ui-renewal/components/atoms/svg';
import { BiLogo } from '@wconcept/wconcept-common/models/renewal/popup';

import * as S from './style';

interface IProps {
  /** type */
  type?: 'main' | 'subPage' | 'subDetail' | undefined;
  /** bgType */
  bgType?: 'white' | 'black' | 'transparent' | undefined;
  /** title */
  title?: string;
  /** titleNode - title 대신 ReactNode를 렌더링할 때 사용 */
  titleNode?: React.ReactNode;
  /** basketCount */
  basketCount?: number;
  /** onClickProfileBtn */
  onClickProfileBtn?: () => void;
  /** onClickBackBtn */
  onClickBackBtn?: () => void;
  /** onClickLogoBtn */
  onClickLogoBtn?: () => void;
  /** onClickHomeBtn */
  onClickHomeBtn?: () => void;
  /** onClickAlarmBtn */
  onClickAlarmBtn?: () => void;
  /** onClickSearchBtn */
  onClickSearchBtn?: () => void;
  /** onClickCartBtn */
  onClickCartBtn?: () => void;
  /** logoInfo */
  logoInfo?: BiLogo;
  /** isProfileBtn */
  isProfileBtn?: boolean;
  /** isProfileBtn */
  isSearchBtn?: boolean;
  /** isProfileBtn */
  isCartBtn?: boolean;
}

const Header = forwardRef(
  (
    {
      type = 'main',
      bgType = 'white',
      title,
      titleNode,
      basketCount = 0,
      onClickProfileBtn,
      onClickBackBtn,
      onClickLogoBtn,
      onClickHomeBtn,
      onClickAlarmBtn,
      onClickSearchBtn,
      onClickCartBtn,
      logoInfo,
      isProfileBtn = true,
      isSearchBtn = true,
      isCartBtn = true,
    }: IProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const [cartCount, setCartCount] = useState<number>(basketCount);

    useEffect(() => {
      setCartCount(Number(basketCount));
    }, [basketCount]);

    // EVENT HANDLER : Profile 클릭
    const onClickProfile = useCallback(() => {
      onClickProfileBtn?.();
    }, [onClickProfileBtn]);

    // EVENT HANDLER : logo 클릭
    const onClickLogo = useCallback(() => {
      onClickLogoBtn?.();
    }, [onClickLogoBtn]);

    // EVENT HANDLER : 알람 클릭
    const onClickAlarm = useCallback(() => {}, []);

    // EVENT HANDLER : 검색 클릭
    const onClickSearch = useCallback(() => {
      onClickSearchBtn?.();
    }, [onClickSearchBtn]);

    // EVENT HANDLER : 장바구니 클릭
    const onClickCart = useCallback(() => {
      onClickCartBtn?.();
    }, [onClickCartBtn]);

    // EVENT HANDLER : onClickBack
    const onClickBack = useCallback(() => {
      onClickBackBtn?.();
    }, [onClickBackBtn]);

    // ITEM RENDERER : getTitle
    const getTitle = useCallback(() => {
      if (titleNode) {
        return titleNode;
      }
      if (title) {
        return (
          <Title>
            <span>{title}</span>
          </Title>
        );
      }
      return null;
    }, [title, titleNode]);

    // ITEM RENDERER : getBtnProfile
    const getBtnProfile = useCallback(() => {
      if (isProfileBtn && onClickProfileBtn) {
        return (
          <Button
            className="btn-profile"
            label="프로필"
            icon={<IconProfile01 />}
            onClick={onClickProfile}
          />
        );
      }
      return null;
    }, [isProfileBtn, onClickProfile, onClickProfileBtn]);

    // ITEM RENDERER : getBtnHome
    const getBtnHome = useCallback(() => {
      if (onClickHomeBtn) {
        return <ModalHomeBtn onClick={onClickHomeBtn} />;
      }
      return null;
    }, [onClickHomeBtn]);

    // ITEM RENDERER : getBtnBack
    const getBtnBack = useCallback(
      () => <ModalBackBtn onClick={onClickBack} />,
      [onClickBack]
    );

    // ITEM RENDERER : getBtnAlarm
    const getBtnAlarm = useCallback(() => {
      if (onClickAlarmBtn) {
        return (
          <Button
            className="btn-alarm dot"
            label="알림"
            icon={<IconAlarm01 />}
            onClick={onClickAlarm}
          />
        );
      }
      return null;
    }, [onClickAlarm, onClickAlarmBtn]);

    // ITEM RENDERER : getBtnSearch
    const getBtnSearch = useCallback(() => {
      if (isSearchBtn && onClickSearchBtn) {
        return (
          <Button
            className="btn-search"
            label="검색"
            icon={<IconSearch01 />}
            onClick={onClickSearch}
          />
        );
      }
      return null;
    }, [isSearchBtn, onClickSearch, onClickSearchBtn]);

    // ITEM RENDERER : getBtnCart
    const getBtnCart = useCallback(() => {
      if (isCartBtn && onClickCartBtn) {
        return (
          <Button
            className="btn-cart"
            label="장바구니 바로가기"
            icon={<IconCart01 />}
            onClick={onClickCart}
          >
            <Count
              label={`${cartCount}개의 상품이 담겨 있습니다.`}
              count={cartCount}
            />
          </Button>
        );
      }
      return null;
    }, [cartCount, isCartBtn, onClickCart, onClickCartBtn]);

    // ITEM RENDERER : getHeaderMain
    const getHeaderMain = useCallback(
      () => (
        <div className="header-main">
          <>
            {logoInfo && (
              <Button
                className="btn-logo type-evt"
                label="W컨셉 이벤트"
                onClick={onClickLogo}
              >
                {logoInfo?.biLogoType === 'LOTTIE_JSON' ? (
                  <Lottie loop path={logoInfo?.imageUrl} play />
                ) : (
                  <Img src={logoInfo?.imageUrl} priority />
                )}
              </Button>
            )}
            {/*  기본로고   */}
            {!logoInfo && (
              <Button
                className="btn-logo"
                label="W컨셉"
                icon={<LogoWcon01 />}
                onClick={onClickLogo}
              />
            )}
          </>

          <div className="area-right">
            {getBtnSearch()}
            {getBtnCart()}
          </div>
        </div>
      ),
      [logoInfo, onClickLogo, getBtnSearch, getBtnCart]
    );

    // ITEM RENDERER : getHeaderSubPage
    const getHeaderSubPage = useCallback(
      () => (
        <div className="header-subPage">
          {getTitle()}
          <div className="area-right">
            {getBtnHome()}
            {getBtnAlarm()}
            {getBtnSearch()}
            {getBtnCart()}
          </div>
        </div>
      ),
      [getTitle, getBtnHome, getBtnAlarm, getBtnSearch, getBtnCart]
    );

    // ITEM RENDERER : getHeaderSubDetail
    const getHeaderSubDetail = useCallback(
      () => (
        <div className="header-subDetail">
          <div className="area-left">{getBtnBack()}</div>
          {getTitle()}
          <div className="area-right">
            {getBtnProfile()}
            {getBtnHome()}
            {getBtnAlarm()}
            {getBtnSearch()}
            {getBtnCart()}
          </div>
        </div>
      ),
      [
        getBtnAlarm,
        getBtnBack,
        getBtnCart,
        getBtnHome,
        getBtnProfile,
        getBtnSearch,
        getTitle,
      ]
    );

    return (
      <S.Header ref={ref} id="header" $bgType={bgType}>
        <div className="inner-header">
          {type === 'main' && getHeaderMain()}
          {type === 'subPage' && getHeaderSubPage()}
          {type === 'subDetail' && getHeaderSubDetail()}
        </div>
      </S.Header>
    );
  }
);

export default memo(Header);
